import React from 'react';

import { OverlayProvider } from '@react-aria/overlays';

import ClickDialogBody from './controls/body';
import type { IProps as IBodyProps } from './controls/body';
import ClickDialogContainer from './controls/container';
import type { IProps as IContainerProps } from './controls/container';
import DialogFooter from './controls/footer';
import type { IProps as IFooterProps } from './controls/footer';
import ClickDialogHeader from './controls/header';
import type { IProps as IHeaderProps } from './controls/header';

export interface IProps
  extends IContainerProps,
    React.PropsWithChildren,
    Omit<IBodyProps, 'className'>,
    Omit<IFooterProps, 'className'>,
    Omit<IHeaderProps, 'className'> {
  bodyClassName?: IBodyProps['className'];
  footerClassName?: IFooterProps['className'];
  headerClassName?: IHeaderProps['className'];
}

const ClickBaseDialog: React.FC<IProps> = React.memo(
  ({
    children,
    bodyClassName,
    centered,
    closable,
    headerClassName,
    footer,
    footerClassName,
    isDismissable,
    isLoading,
    isSaving,
    isSecure,
    isOpen,
    shouldCloseOnBlur,
    spinnerText,
    subTitle,
    title,
    overlayClassName,
    underlayClassName,
    onClose
  }) => {
    return (
      <OverlayProvider>
        <ClickDialogContainer
          centered={centered}
          isDismissable={isDismissable}
          isSecure={isSecure}
          isOpen={isOpen}
          overlayClassName={overlayClassName}
          shouldCloseOnBlur={shouldCloseOnBlur}
          underlayClassName={underlayClassName}
          onClose={onClose}
        >
          {!!title && (
            <ClickDialogHeader
              className={headerClassName}
              closable={closable}
              subTitle={subTitle}
              title={title}
              onClose={onClose}
            />
          )}
          <ClickDialogBody
            className={bodyClassName}
            isLoading={isLoading}
            spinnerText={spinnerText}
          >
            {children}
          </ClickDialogBody>
          {!!footer && !isLoading && (
            <DialogFooter className={footerClassName} footer={footer} isSaving={isSaving} />
          )}
        </ClickDialogContainer>
      </OverlayProvider>
    );
  }
);

ClickBaseDialog.displayName = 'ClickBaseDialog';

export default ClickBaseDialog;
